import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { API, opts, SOURCE_META, fmtDate, TierChip, customerLink, orderLink } from './shared';

const H = ({ children }) => <p className="font-mono text-xs text-[#39FF14] font-bold uppercase mt-5 mb-2">{children}</p>;
const Empty = ({ t }) => <p className="font-mono text-[11px] text-[#4B6B52]">{t}</p>;

function Timeline({ rows }) {
  if (!rows.length) return <Empty t="// no points activity in this period" />;
  return (
    <div className="space-y-1" data-testid="watch-audit-timeline">
      {rows.map((e, i) => (
        <div key={i} className="flex items-start gap-2 border-b border-[#2A0F3D]/60 py-1.5 font-mono text-[11px]">
          <span className="w-5">{SOURCE_META[e.bucket]}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[#A3C7A8] break-words">{e.reason}{e.related_name ? ` · ${e.related_name}` : ''}</p>
            <p className="text-[#4B6B52]">{fmtDate(e.created_at)} · {e.source}{e.order_id && <> · <Link to={orderLink(e.order_id)} className="text-[#00F0FF] underline" data-testid={`watch-order-link-${e.order_id}`}>#{e.order_id.slice(0, 8)}</Link></>}</p>
          </div>
          <span className={`font-bold ${e.delta >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>{e.delta > 0 ? '+' : ''}{e.delta}</span>
        </div>
      ))}
    </div>
  );
}

function Referrals({ rows }) {
  if (!rows.length) return <Empty t="// no referrals" />;
  const paying = rows.filter((r) => r.paying).length;
  return (
    <div data-testid="watch-audit-referrals">
      <p className="font-mono text-[11px] text-[#A3C7A8] mb-1">{paying}/{rows.length} paying ({Math.round((100 * paying) / rows.length)}%)</p>
      {rows.map((r) => (
        <div key={r.user_id} className="flex justify-between gap-2 font-mono text-[11px] py-1 border-b border-[#2A0F3D]/60">
          <span className="text-[#A3C7A8] truncate">{r.name} <span className="text-[#4B6B52]">· joined {fmtDate(r.joined)}</span></span>
          <span className={r.paying ? 'text-[#39FF14]' : 'text-[#FF9F0A]'}>{r.paying ? `PAYING · ${r.delivered_orders} orders · $${r.spent.toFixed(2)}` : 'NOT YET'}</span>
        </div>
      ))}
    </div>
  );
}

function Likes({ rows, received }) {
  if (!rows.length) return <Empty t={received ? '// no likes received' : '// no likes given'} />;
  return (
    <div data-testid={received ? 'watch-audit-likes-received' : 'watch-audit-likes-given'}>
      {rows.map((r) => (
        <div key={r.user_id} className={`flex justify-between gap-2 font-mono text-[11px] py-1 border-b border-[#2A0F3D]/60 ${r.mutual ? 'bg-[#B400FF]/10' : ''}`}>
          <span className="text-[#A3C7A8] truncate">{r.name || r.user_id.slice(0, 8)}{r.mutual && <span className="text-[#B400FF]"> · 🔁 MUTUAL</span>}</span>
          <span className="text-[#FF6B00]">🔥 {r.count}{received ? ` · ${r.pts_paid} pts paid` : ''}</span>
        </div>
      ))}
    </div>
  );
}

export function WatchAuditSheet({ userId, days, onClose, isSA }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    setData(null);
    axios.get(`${API}/user/${userId}`, { ...opts, params: { days } }).then(({ data: d }) => setData(d)).catch(() => setData({ error: true }));
  }, [userId, days]);
  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" hideCloseButton className="w-full sm:max-w-xl bg-[#0C0612] border-l border-[#2A0F3D] overflow-y-auto pb-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' }} data-testid="watch-audit-sheet">
        <SheetHeader>
          <SheetTitle className="font-mono text-[#39FF14] text-left flex items-center gap-2 flex-wrap">
            {data?.user ? <Link to={customerLink(data.user.id)} className="underline underline-offset-2 hover:text-[#00F0FF]" data-testid="watch-audit-customer-link">{data.user.name || data.user.email}</Link> : 'Loading…'}
            {data?.user && <TierChip tier={data.user.tier} testId="watch-audit-tier" />}
          </SheetTitle>
          {data?.user && <p className="font-mono text-[11px] text-[#6B8E73] text-left">{data.user.email} · {data.user.region?.toUpperCase()} · last {days} days</p>}
        </SheetHeader>
        {data?.error && <Empty t="// could not load audit trail" />}
        {data?.timeline && (
          <>
            <H>Points timeline</H><Timeline rows={data.timeline} />
            <H>Referrals</H><Referrals rows={data.referrals} />
            <H>Likes received (who likes them)</H><Likes rows={data.likes_received} received />
            <H>Likes given (who they like)</H><Likes rows={data.likes_given} />
            {isSA && <Link to="/dispatch/admin/points" className="inline-block mt-5 font-mono text-xs text-[#00F0FF] underline" data-testid="watch-audit-adjust-link">Adjust points in Points admin →</Link>}
          </>
        )}
        <div className="sticky bottom-0 -mx-6 px-6 pt-3 mt-6 bg-[#0C0612] border-t border-[#2A0F3D]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
          <Button type="button" onClick={onClose} variant="outline" className="w-full h-11 bg-transparent border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] hover:text-[#FF3B30] rounded-none font-mono uppercase tracking-wider" data-testid="watch-audit-close">
            ✕ CLOSE
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
